<template>
    <div class="flex items-center gap-2">
        <button
            type="button"
            class="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            :class="{ 'border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100': isLive }"
            :disabled="isConnecting || disabled"
            data-testid="voice-call-button"
            :aria-label="ariaLabel"
            @click="onToggle">
            <span v-if="isConnecting">Connecting…</span>
            <span v-else-if="isLive">End call</span>
            <span v-else>🎙 Talk</span>
        </button>
        <span v-if="isLive" class="flex items-center gap-1 text-xs text-rose-600">
            <span class="h-2 w-2 animate-pulse rounded-full bg-rose-500"></span>
            <span>Live</span>
        </span>
        <span v-else-if="hasError" class="max-w-md text-xs text-rose-600" data-testid="voice-call-error" :title="error ?? ''">
            {{ error }}
        </span>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { useVoiceSession, type VoiceStreamEvent } from "./useVoiceSession";

const props = defineProps<{
    conversationId: string;
    disabled?: boolean;
}>();

const emit = defineEmits<{
    (e: "ended"): void;
    (e: "stream-event", payload: VoiceStreamEvent): void;
}>();

const { state, error, start, stop } = useVoiceSession({
    onStreamEvent: (event) => emit("stream-event", event),
});

const isConnecting = computed(() => state.value === "connecting");
const isLive = computed(() => state.value === "live");
const hasError = computed(() => state.value === "error");

const ariaLabel = computed(() => {
    if (isLive.value) {
        return "End voice call with Nadia";
    }
    return "Start voice call with Nadia";
});

const onToggle = async () => {
    if (isLive.value || isConnecting.value) {
        await stop();
        emit("ended");
        return;
    }
    await start(props.conversationId);
};
</script>
